import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  Slider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import {
  DirectionsWalk as MobilityIcon,
  Favorite as HealthIcon,
  Chat as CommunicationIcon,
  Mood as ActivityIcon,
  EmojiEmotions as EmotionalIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { QK } from "../../../../../../base/qk";
import {
  getWellbeingByID,
  updateWellbeing,
} from "../../../../../../api/services/observations";
import { enqueueSnackbar } from "notistack";
import CustomDateField from "../../../../../FormFields/CustomDateField";

const EditWellbeingModal = ({ show, handleClose, selectedID }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    mobility: 0,
    health: 0,
    communication: 0,
    activity: 0,
    emotional_state: 0,
    date_time: "",
  });

  const { data: wellbeing, isLoading, refetch } = useQuery(
    [QK.wellbeing, selectedID],
    () => getWellbeingByID(selectedID),
    {
      enabled: false,
      onSuccess: (data) => {
        if (data) {
          setFormData({
            mobility: data.mobility || 0,
            health: data.health || 0,
            communication: data.communication || 0,
            activity: data.activity || 0,
            emotional_state: data.emotional_state || 0,
            date_time: data.date_time || "",
          });
        }
      },
    }
  );

  useEffect(() => {
    if (show && selectedID) {
      refetch();
    }
  }, [show, selectedID, refetch]);

  const updateMutation = useMutation(
    (newData) => updateWellbeing(selectedID, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Wellbeing entry updated successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.wellbeings]);
        queryClient.invalidateQueries([QK.wellbeing, selectedID]);
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(error?.response?.data?.message || "Failed to update wellbeing entry", {
          variant: "error",
        });
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSliderChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleDialogClose = () => {
    setFormData({
      mobility: 0,
      health: 0,
      communication: 0,
      activity: 0,
      emotional_state: 0,
      date_time: "",
    });
    handleClose();
  };

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const sliderFields = [
    {
      key: "mobility",
      label: "Mobility",
      icon: <MobilityIcon sx={{ color: "#2469F0", fontSize: 16 }} />,
      accent: "#2469F0",
    },
    {
      key: "health",
      label: "Health",
      icon: <HealthIcon sx={{ color: "#10B981", fontSize: 16 }} />,
      accent: "#10B981",
    },
    {
      key: "communication",
      label: "Communication",
      icon: <CommunicationIcon sx={{ color: "#F59E0B", fontSize: 16 }} />,
      accent: "#F59E0B",
    },
    {
      key: "activity",
      label: "Activity",
      icon: <ActivityIcon sx={{ color: "#8B5CF6", fontSize: 16 }} />,
      accent: "#8B5CF6",
    },
    {
      key: "emotional_state",
      label: "Emotional",
      icon: <EmotionalIcon sx={{ color: "#EF4444", fontSize: 16 }} />,
      accent: "#EF4444",
    },
  ];

  const cardSx = {
    p: 1.75,
    borderRadius: "10px",
    border: "1px solid #EAECF0",
    bgcolor: "#FFFFFF",
    transition: "all 0.2s ease",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 200px",
    minWidth: 0,
  };

  return (
    <Dialog
      open={show}
      onClose={handleDialogClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: "#101828",
                  letterSpacing: "-0.01em",
                }}
              >
                Edit Wellbeing Entry
              </Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "#667085", mt: 0.2 }}>
                Update wellbeing record details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              color: "#98A2B3",
              "&:hover": { bgcolor: "#F2F4F7", color: "#475467" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: "#EAECF0" }} />
      </DialogTitle>

      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              width: "100%",
            }}
          >
            {sliderFields.map((field) => {
              const value = formData[field.key] || 0;
              const percentage = Math.round(value * 100);
              return (
                <Box
                  key={field.key}
                  sx={{
                    ...cardSx,
                    "&:hover": {
                      borderColor: alpha(field.accent, 0.4),
                      boxShadow: `0px 2px 8px ${alpha(field.accent, 0.08)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "7px",
                        bgcolor: alpha(field.accent, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {field.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#344054",
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: field.accent,
                        ml: "auto",
                      }}
                    >
                      {percentage}%
                    </Typography>
                  </Box>
                  <Box sx={{ mt: "auto" }}>
                    <Slider
                      value={value}
                      onChange={(_, v) => handleSliderChange(field.key, v)}
                      min={0}
                      max={0.8}
                      step={0.2}
                      marks={[
                        { value: 0, label: "0" },
                        { value: 0.2, label: "0.2" },
                        { value: 0.4, label: "0.4" },
                        { value: 0.6, label: "0.6" },
                        { value: 0.8, label: "0.8" },
                      ]}
                      sx={{
                        color: field.accent,
                        py: 0.5,
                        "& .MuiSlider-thumb": {
                          width: 14,
                          height: 14,
                          boxShadow: `0 0 0 4px ${alpha(field.accent, 0.15)}`,
                          "&:hover": {
                            boxShadow: `0 0 0 6px ${alpha(field.accent, 0.2)}`,
                          },
                        },
                        "& .MuiSlider-rail": {
                          bgcolor: "#F2F4F7",
                          opacity: 1,
                        },
                        "& .MuiSlider-mark": {
                          bgcolor: "#D0D5DD",
                          height: 4,
                          width: 4,
                          borderRadius: "50%",
                        },
                        "& .MuiSlider-markLabel": {
                          fontSize: "0.6rem",
                          color: "#98A2B3",
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Box>
                </Box>
              );
            })}

            <Box
              sx={{
                ...cardSx,
                "&:hover": {
                  borderColor: alpha("#667085", 0.4),
                  boxShadow: `0px 2px 8px ${alpha("#667085", 0.08)}`,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "7px",
                    bgcolor: alpha("#667085", 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CalendarIcon sx={{ color: "#667085", fontSize: 16 }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#344054",
                  }}
                >
                  Date & Time
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    color: "#F04438",
                    ml: "auto",
                  }}
                >
                  Required
                </Typography>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <CustomDateField
                  name="date_time"
                  label=""
                  value={formData.date_time || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: "#EAECF0" }} />

        <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            disabled={updateMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              borderRadius: "8px",
              borderColor: "#D0D5DD",
              color: "#344054",
              px: 2.5,
              height: 38,
              "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isLoading}
            startIcon={
              updateMutation.isLoading ? (
                <CircularProgress size={16} sx={{ color: "#FFFFFF" }} />
              ) : (
                <SaveIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              borderRadius: "8px",
              bgcolor: "#2469F0",
              height: 38,
              px: 3,
              boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 6px rgba(36, 105, 240, 0.25)",
              },
            }}
          >
            {updateMutation.isLoading ? "Saving..." : "Update Wellbeing"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditWellbeingModal;